import { useCallback } from "react";

import { useSetAtom } from "../editor-jotai";

import { recordingStateAtom } from "./atoms";
import { createCompositorCanvas, drawFrame } from "./canvasCompositor";
import {
  initCursorCapture,
  drawCursor,
  cleanupCursorCapture,
} from "./cursorCapture";
import {
  initSvgCapture,
  updateSvgCapture,
  drawSvgLayer,
  cleanupSvgCapture,
} from "./svgLayerCapture";
import {
  createBackgroundBlurPipeline,
  isBackgroundBlurSupported,
} from "./backgroundBlurPipeline";
import { getCameraStream, waitForVideoReady } from "./cameraStreamHelper";

import type { BackgroundBlurPipeline } from "./backgroundBlurPipeline";
import type { AppClassProperties } from "../types";
import type { RecordingSettings, CameraPosition } from "./types";

// Module-level state — survives component unmounts
let mediaRecorder: MediaRecorder | null = null;
let chunks: Blob[] = [];
let rafId = 0;
let timerInterval: ReturnType<typeof setInterval> | null = null;
let streamsToStop: MediaStream[] = [];
let blurPipeline: BackgroundBlurPipeline | null = null;

const cleanupRecordingResources = () => {
  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = 0;
  }
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
  for (const stream of streamsToStop) {
    for (const track of stream.getTracks()) {
      track.stop();
    }
  }
  streamsToStop = [];
  blurPipeline?.destroy();
  blurPipeline = null;
  cleanupCursorCapture();
  cleanupSvgCapture();
};

export const useRecording = () => {
  const setRecordingState = useSetAtom(recordingStateAtom);

  const startRecording = useCallback(
    async (
      app: AppClassProperties,
      settings: RecordingSettings,
      cameraPosition: CameraPosition,
      cameraSize: number,
    ) => {
      chunks = [];

      try {
        let videoStream: MediaStream;

        if (!settings.immersiveMode) {
          const displayStream = await navigator.mediaDevices.getDisplayMedia({
            video: {
              displaySurface: "browser",
            } as MediaTrackConstraints,
            audio: false,
            preferCurrentTab: true,
          } as DisplayMediaStreamOptions);

          streamsToStop.push(displayStream);
          videoStream = displayStream;
        } else {
          const staticCanvas = app.canvas;
          const interactiveCanvas = app.interactiveCanvas;
          const width = staticCanvas.width;
          const height = staticCanvas.height;

          const { canvas: offscreen, ctx } = createCompositorCanvas(
            width,
            height,
          );

          initSvgCapture();
          initCursorCapture();

          let cameraVideo: HTMLVideoElement | null = null;
          let cameraSource: HTMLVideoElement | null = null;

          if (settings.cameraEnabled) {
            try {
              const cameraStream = await getCameraStream(
                settings.cameraDeviceId,
              );
              streamsToStop.push(cameraStream);

              let cameraDisplayStream: MediaStream = cameraStream;
              if (settings.backgroundBlur && isBackgroundBlurSupported()) {
                try {
                  const pipeline =
                    await createBackgroundBlurPipeline(cameraStream);
                  blurPipeline = pipeline;
                  cameraDisplayStream = pipeline.outputStream;
                } catch (err) {
                  console.error("[Recording] Blur pipeline init failed:", err);
                }
              }

              cameraVideo = document.createElement("video");
              cameraVideo.srcObject = cameraDisplayStream;
              cameraVideo.muted = true;
              cameraVideo.playsInline = true;
              await cameraVideo.play();
              await waitForVideoReady(cameraVideo);

              cameraSource = cameraVideo;
            } catch (err) {
              console.error("[Recording] Camera init failed:", err);
              cameraVideo = null;
              cameraSource = null;
            }
          }

          const dpr = window.devicePixelRatio;

          const compositeLoop = () => {
            const newElCanvas = document.querySelector<HTMLCanvasElement>(
              ".excalidraw__canvas:not(.interactive):not(.static)",
            );
            updateSvgCapture();

            const scaledPos = {
              x:
                cameraPosition.x >= 0
                  ? cameraPosition.x * dpr
                  : cameraPosition.x,
              y:
                cameraPosition.y >= 0
                  ? cameraPosition.y * dpr
                  : cameraPosition.y,
            };
            const scaledCameraSize = cameraSize * dpr;

            drawFrame(
              ctx,
              width,
              height,
              staticCanvas,
              newElCanvas,
              interactiveCanvas,
              drawSvgLayer,
              drawCursor,
              cameraSource,
              scaledPos,
              settings.cameraShape,
              settings.cameraMirrored,
              scaledCameraSize,
            );
            rafId = requestAnimationFrame(compositeLoop);
          };
          compositeLoop();

          const canvasStream = offscreen.captureStream(30);
          streamsToStop.push(canvasStream);
          videoStream = canvasStream;
        }

        const combinedTracks: MediaStreamTrack[] = [
          ...videoStream.getVideoTracks(),
        ];

        if (settings.micEnabled && settings.micDeviceId) {
          let audioStream: MediaStream | null = null;
          try {
            audioStream = await navigator.mediaDevices.getUserMedia({
              audio: { deviceId: { exact: settings.micDeviceId } },
            });
          } catch (exactError) {
            try {
              audioStream = await navigator.mediaDevices.getUserMedia({
                audio: { deviceId: settings.micDeviceId },
              });
            } catch (fallbackError) {
              // both exact and fallback deviceId failed
            }
          }
          if (audioStream) {
            streamsToStop.push(audioStream);
            combinedTracks.push(...audioStream.getAudioTracks());
          }
        }

        const combinedStream = new MediaStream(combinedTracks);

        let mimeType = "video/webm;codecs=vp9";
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = "video/webm";
        }

        const recorder = new MediaRecorder(combinedStream, { mimeType });

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            chunks.push(e.data);
          }
        };

        recorder.onstop = () => {
          const blob = new Blob(chunks, { type: "video/webm" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `excalidraw-recording-${Date.now()}.webm`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          chunks = [];
        };

        mediaRecorder = recorder;
        recorder.start(1000);

        timerInterval = setInterval(() => {
          setRecordingState((prev) => ({
            ...prev,
            elapsedSeconds: prev.elapsedSeconds + 1,
          }));
        }, 1000);

        setRecordingState((prev) => ({
          ...prev,
          status: "recording",
          elapsedSeconds: 0,
        }));
      } catch (error) {
        cleanupRecordingResources();
        setRecordingState((prev) => ({ ...prev, status: "idle" }));
        throw error;
      }
    },
    [setRecordingState],
  );

  const stopRecording = useCallback(() => {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
      mediaRecorder = null;
    }
    cleanupRecordingResources();
    setRecordingState((prev) => ({
      ...prev,
      status: "idle",
      elapsedSeconds: 0,
    }));
  }, [setRecordingState]);

  return { startRecording, stopRecording };
};
